<template>
    <div class="custom-table-items">
        <div v-if="loading" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Yükleniyor...</span>
            </div>
        </div>

        <table class="table">
            <thead class="thead-light">
                <tr>
                    <th>{{ $t('views.transactions.columns.date') }}</th>
                    <th>{{ $t('views.transactions.columns.description') }}</th>
                    <th>{{ $t('views.transactions.columns.category') }}</th>
                    <th>{{ $t('views.transactions.columns.amount') }}</th>
                    <th>{{ $t('views.transactions.columns.totalBalance') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="!loading && error">
                    <td colspan="5" class="text-center py-4 text-danger">
                        {{ error }}
                    </td>
                </tr>
                <tr v-else-if="!loading && summaryData.length === 0">
                    <td colspan="5" class="text-center py-4">
                        {{ $t('views.transactions.noData') }}
                    </td>
                </tr>
                <tr v-for="(item, index) in summaryData" :key="index">
                    <td>{{ formatDate(item.transaction_date) }}</td>
                    <td>
                        <a v-if="item.hasDetail === 1" class="text-primary text-decoration-underline" href="javascript:;" @click="viewDetails(item.id || item.transaction_id)">{{ item.reason || item.reason_detail }}</a>
                        <span v-else>{{ item.reason || item.reason_detail }}</span>
                    </td>
                    <td>{{ item.category }}</td>
                    <td>
                        <div class="d-flex" style="gap: 5px">
                            <span>{{ item.currency || currencySymbol }}</span>
                            <strong class="text-secondary"> {{ item.amount >= 0 ? '+' : '-' }}{{ formatNumber(Math.abs(item.amount)) }} </strong>
                            <Popper v-if="item.currency_original && item.currency_original !== item.currency" class="light-popper" hover placement="bottom" arrow style="margin: 0; border: 0">
                                <i class="bi bi-arrow-left-right fs-6 text-muted convert-icon"></i>
                                <template #content>
                                    <span class="fw-normal">
                                        {{ item.currency_original }} <strong>{{ item.amount_original >= 0 ? '+' : '-' }}{{ formatNumber(Math.abs(item.amount_original)) }}</strong>
                                    </span>
                                </template>
                            </Popper>
                        </div>
                    </td>
                    <td>
                        <div class="d-flex" style="gap: 5px">
                            <span>{{ item.currency || currencySymbol }}</span>
                            <strong class="text-secondary"> {{ item.running_balance >= 0 ? '+' : '-' }}{{ formatNumber(Math.abs(item.running_balance)) }}</strong>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import { countryInfo } from '@/utils/helper'
import Popper from 'vue3-popper'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
    name: 'TransactionsSummary',
    components: {
        Popper,
    },
    data() {
        return {}
    },
    computed: {
        ...mapState('transactions', {
            summaryState: (state) => state.summary,
            filterParams: (state) => state.filterParams,
        }),
        ...mapGetters('market', ['currentMarket']),

        summaryData() {
            return this.summaryState.data?.items || []
        },
        loading() {
            return this.summaryState.loading
        },
        error() {
            return this.summaryState.error
        },
        currencySymbol() {
            // countryInfo fonksiyonundan ülkeleri al
            const countries = countryInfo()

            // currentMarket varsa ve short özelliği varsa, ilgili para birimini getir
            if (this.currentMarket && this.currentMarket.short) {
                return countries[this.currentMarket.short].symbol || countries[this.currentMarket.short].currency_text || 'USD'
            }

            // Varsayılan olarak USD dön
            return 'USD'
        },
    },
    created() {
        // Summary verilerini yükle
        this.loadSummary()
    },
    methods: {
        ...mapActions('transactions', ['getTransactionsSummary']),

        loadSummary() {
            console.log('Özet verileri yükleniyor...')
            this.getTransactionsSummary()
        },
        formatNumber(number) {
            // Sayı değerini güvenli bir şekilde işleme
            const numValue = parseFloat(number) || 0
            return numValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        formatDate(dateString) {
            // ISO tarih formatından dd.mm.yyyy formatına çevirme
            if (!dateString) return ''

            const date = new Date(dateString)
            const day = date.getDate().toString().padStart(2, '0')
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const year = date.getFullYear()

            return `${day}.${month}.${year}`
        },
        viewDetails(id) {
            if (!id) return

            this.$router.push({
                name: 'transactions-details',
                params: {
                    id,
                    type: 'summary',
                },
            })
        },
    },
    watch: {
        // Filtre parametreleri değiştiğinde verileri yenile
        'filterParams.dateRange': {
            handler() {
                this.loadSummary()
            },
            deep: true,
        },
        'filterParams.search': function () {
            this.loadSummary()
        },
        'filterParams.categoryFilter': function () {
            this.loadSummary()
        },
    },
}
</script>

<style lang="scss" scoped>
.custom-table-items {
    max-width: 100% !important;
    overflow: visible;
    min-height: 400px !important;
    position: relative;

    table {
        thead {
            top: 0;
            position: sticky;
            z-index: 10;
        }
    }

    tr {
        border-bottom: 1px solid #e7eaf3b2;
    }

    td {
        vertical-align: middle;
        height: 50px;
        padding-top: 8px;
        padding-bottom: 8px;
        color: #677788 !important;
        font-size: 0.8rem;
        font-weight: 400;

        strong {
            font-weight: 600;
        }
    }
}

.convert-icon {
    cursor: pointer;
    margin-left: 3px;
    opacity: 0.8;
    color: #ffb53e !important;
    &:hover {
        opacity: 1;
    }
}

.light-popper {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
}

.thead-light {
    th {
        background-color: #f8fafd !important;
        border-bottom: 1px solid #e7eaf3b2 !important;
        color: #677788 !important;
        font-size: 11.9px !important;
        font-weight: 400 !important;
        line-height: 17.85px;
        font-weight: bold !important;
    }
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
}
</style>
